import { atom } from 'jotai';
import isEqual from 'lodash/isEqual';

import { brokeredOrderFileConfirmUploadAtom } from '../_graphql/mutationBrokeredOrderFileConfirmUpload';
import { brokeredOrderFileCreateMutationAtom } from '../_graphql/mutationBrokeredOrderFileCreate';
import { brokeredOrderFileDeleteMutationAtom } from '../_graphql/mutationBrokeredOrderFileDelete';
import { BrokeredOrderDetailRecord } from '../types/brokeredOrder';
import { BrokeredOrderLineItemRecord } from '../types/brokeredOrderLineItem';

export type BrokeredOrderFormState = {
  notes: string;
  purchaseOrderIds: string[];
  salesOrderIds: string[];
  lineItems: BrokeredOrderLineItemRecord[];
};

const initialState = (): BrokeredOrderFormState => ({
  notes: '',
  purchaseOrderIds: [],
  salesOrderIds: [],
  lineItems: [],
});

const mapOrderToFormState = (
  order: BrokeredOrderDetailRecord,
): BrokeredOrderFormState => ({
  notes: order.notes ?? '',
  purchaseOrderIds: [],
  salesOrderIds: [],
  lineItems: [],
});

// 🔘🔘🔘 PLAIN ATOMS
export const $brokeredOrderForm = atom<BrokeredOrderFormState>(initialState());
export const $discardBaseline = atom<BrokeredOrderFormState | null>(null);
export const $lastPopulatedOrderId = atom<string | undefined>(undefined);
export const $isBrokeredOrderFormPopulated = atom<boolean>(false);

// 🟢🟢🟢 COMPUTED DATA -> has unsaved changes
export const $hasUnsavedChanges = atom((get) => {
  const form = get($brokeredOrderForm);
  const baseline = get($discardBaseline);

  if (!baseline) return false;

  return !isEqual(form, baseline);
});

// 🟠🟠🟠 ACTION -> update form fields
export const $updateBrokeredOrderForm = atom(
  null,
  (get, set, update: Partial<BrokeredOrderFormState>) => {
    set($brokeredOrderForm, { ...get($brokeredOrderForm), ...update });
  },
);

// 🟠🟠🟠 ACTION -> populate form from order
export const $populateBrokeredOrderFormFromOrder = atom(
  null,
  (get, set, order: BrokeredOrderDetailRecord) => {
    const formState = mapOrderToFormState(order);
    set($brokeredOrderForm, formState);
    set($discardBaseline, formState);
    set($lastPopulatedOrderId, order.id);
    set($isBrokeredOrderFormPopulated, true);
  },
);

// 🟠🟠🟠 ACTION -> reset form to baseline
export const $resetBrokeredOrderFormToOrder = atom(null, (get, set) => {
  const baseline = get($discardBaseline);
  if (baseline) {
    set($brokeredOrderForm, baseline);
  }
});

// 🟠🟠🟠 ACTION -> populate form (with order change detection)
export const $populateBrokeredOrderFormAction = atom(
  null,
  (get, set, order: BrokeredOrderDetailRecord | null | undefined) => {
    if (!order) return;

    const currentOrderId = order.id;
    const lastOrderId = get($lastPopulatedOrderId);

    if (currentOrderId !== lastOrderId) {
      set($isBrokeredOrderFormPopulated, false);
    }

    if (!get($isBrokeredOrderFormPopulated)) {
      set($populateBrokeredOrderFormFromOrder, order);
    }
  },
);

// 🟠🟠🟠 ACTION -> reset form to initial state (for create dialog)
export const $resetBrokeredOrderForm = atom(null, (_, set) => {
  set($brokeredOrderForm, initialState());
  set($discardBaseline, null);
  set($lastPopulatedOrderId, undefined);
  set($isBrokeredOrderFormPopulated, false);
});

// 🟠🟠🟠 ACTION -> add purchase order ID
export const $addPurchaseOrderId = atom(null, (get, set, orderId: string) => {
  const form = get($brokeredOrderForm);
  if (!form.purchaseOrderIds.includes(orderId)) {
    set($brokeredOrderForm, {
      ...form,
      purchaseOrderIds: [...form.purchaseOrderIds, orderId],
    });
  }
});

// 🟠🟠🟠 ACTION -> remove purchase order ID
export const $removePurchaseOrderId = atom(
  null,
  (get, set, orderId: string) => {
    const form = get($brokeredOrderForm);
    set($brokeredOrderForm, {
      ...form,
      purchaseOrderIds: form.purchaseOrderIds.filter((id) => id !== orderId),
    });
  },
);

// 🟠🟠🟠 ACTION -> add sales order ID
export const $addSalesOrderId = atom(null, (get, set, orderId: string) => {
  const form = get($brokeredOrderForm);
  if (!form.salesOrderIds.includes(orderId)) {
    set($brokeredOrderForm, {
      ...form,
      salesOrderIds: [...form.salesOrderIds, orderId],
    });
  }
});

// 🟠🟠🟠 ACTION -> remove sales order ID
export const $removeSalesOrderId = atom(null, (get, set, orderId: string) => {
  const form = get($brokeredOrderForm);
  set($brokeredOrderForm, {
    ...form,
    salesOrderIds: form.salesOrderIds.filter((id) => id !== orderId),
  });
});

// 🟠🟠🟠 ACTION -> create brokered order file
export const $createBrokeredOrderFile = brokeredOrderFileCreateMutationAtom();

// 🟠🟠🟠 ACTION -> confirm brokered order file upload
export const $confirmBrokeredOrderFileUpload =
  brokeredOrderFileConfirmUploadAtom();

// 🟠🟠🟠 ACTION -> delete brokered order file
export const $deleteBrokeredOrderFile = brokeredOrderFileDeleteMutationAtom();
